<template>
  <div class="supersearch-quick-panel" :class="{ 'supersearch-quick-panel--inline': inline }" ref="panelRef">
    <!-- Loading State -->
    <div v-if="loading" class="supersearch-panel-loading">
      <BaseIcon size="medium" variant="ghost" class="animate-spin">
        <LoaderIcon />
      </BaseIcon>
      <span class="text-sm text-text-muted">Searching...</span>
    </div>

    <!-- Results -->
    <div v-else-if="filteredDomains.length > 0" class="supersearch-panel-results">
      <BaseScrollbar
        class="supersearch-panel-results-scroll"
        :max-height="inline ? undefined : 'calc(70vh - 4.5rem)'"
        :update-key="filteredDomains.length"
        :options="{ wheelSpeed: 1, wheelPropagation: false, suppressScrollX: true, suppressScrollY: false }"
      >
        <div
          v-for="domain in filteredDomains"
          :key="domain.domain"
          class="supersearch-domain-section"
        >
          <div class="supersearch-domain-header">
            <span class="supersearch-domain-label">
              {{ getDomainLabel(domain.domain) }}
            </span>
            <span class="supersearch-domain-count">({{ domain.results.length }})</span>
          </div>
          
          <div class="supersearch-domain-items">
            <div
              v-for="(item, index) in domain.results.slice(0, 3)"
              :key="index"
              class="supersearch-item"
              @mousedown.prevent="handleItemMousedown(item, domain.domain)"
            >
              <div class="supersearch-item-primary">
                {{ getItemDisplayText(item.item) }}
              </div>
              <div v-if="item.item.parent_name" class="supersearch-item-context text-xs text-text-muted">
                {{ formatParentType(item.item.parent_type) }}: {{ item.item.parent_name }}
              </div>
              <div v-if="getItemSecondaryText(item.item, domain.domain)" class="supersearch-item-secondary">
                {{ getItemSecondaryText(item.item, domain.domain) }}
              </div>
            </div>
          </div>
        </div>
      </BaseScrollbar>
    </div>

    <!-- No Results -->
    <div v-else class="supersearch-panel-empty">
      <div class="supersearch-panel-empty-content">
        <span class="text-sm text-text-muted">No results found</span>
      </div>
    </div>

    <!-- Footer - Show All Results Button -->
    <div class="supersearch-panel-footer">
      <BaseButton
        variant="secondary"
        size="small"
        class="w-full"
        @click="handleShowAllResults"
      >
        Show all results
      </BaseButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { usePermissions } from '@/composables/usePermissions'
import { useAuthStore } from '@/stores/auth'
import { PERMISSIONS } from '@/constants/permissions'
import type { SupersearchDomain, SupersearchItem } from '@/types/supersearch'
import { SUPERSEARCH_DOMAIN_LABELS } from '@/types/supersearch'
import { convertApiLinkToRoute, getItemDisplayText, getItemSecondaryText } from '@/utils/supersearch'
import { getNoteRoute, getTaskRoute, getProjectRoute } from '@/utils/routeHelpers'
import BaseIcon from '@/components/ui/BaseIcon.vue'
import BaseIconButton from '@/components/navigation/BaseIconButton.vue'
import BaseButton from '@/components/navigation/BaseButton.vue'
import BaseScrollbar from '@/components/layout/BaseScrollbar.vue'
import { X, Loader2 } from 'lucide-vue-next'

const XIcon = X
const LoaderIcon = Loader2

interface Props {
  query: string
  results: SupersearchDomain[]
  loading: boolean
  /** When true, panel flows in layout (e.g. inside flyout); when false, positioned absolute below trigger */
  inline?: boolean
}

const props = withDefaults(defineProps<Props>(), { inline: false })

const emit = defineEmits<{
  close: []
  'selecting-start': []
  'selecting-end': []
}>()

const router = useRouter()
const { hasPermission } = usePermissions()
const panelRef = ref<HTMLElement | null>(null)

/**
 * Get domain label
 */
const getDomainLabel = (domain: string): string => {
  return SUPERSEARCH_DOMAIN_LABELS[domain] || domain
}

/**
 * Split notes, tasks, and projects into personal vs CRM based on context
 */
const splitDomainsByContext = (domains: SupersearchDomain[]): SupersearchDomain[] => {
  const splitDomains: SupersearchDomain[] = []
  const authStore = useAuthStore()
  const currentUserUuid = computed(() => authStore.user?.uuid)
  
  // First pass: collect personal projects to identify notes/tasks that belong to them
  const personalProjectUuids = new Set<string>()
  
  for (const domain of domains) {
    if (domain.domain === 'projects') {
      domain.results.forEach(item => {
        const isPersonal = item.item.context === 'personal' ||
                          (item.item.parent_type === 'user' && 
                           item.item.parent_uuid === currentUserUuid.value)
        if (isPersonal && item.item.uuid) {
          personalProjectUuids.add(item.item.uuid)
        }
      })
    }
  }
  
  // Second pass: split domains
  for (const domain of domains) {
    if (domain.domain === 'notes') {
      // Split notes into personal-notes and notes (CRM)
      const personalNotes: SupersearchItem[] = []
      const crmNotes: SupersearchItem[] = []
      
      domain.results.forEach(item => {
        const context = item.item.context
        // Check if it's a personal note:
        // 1. Explicit context === 'personal'
        // 2. Note belongs to a personal project (parent_type === 'project' and parent_uuid is in personalProjectUuids)
        const isPersonal = context === 'personal' ||
                          (item.item.parent_type === 'project' && 
                           item.item.parent_uuid && 
                           personalProjectUuids.has(item.item.parent_uuid))
        
        if (isPersonal) {
          personalNotes.push(item)
        } else {
          // All other notes (crm, flash_report, or no context) go to CRM notes
          crmNotes.push(item)
        }
      })
      
      if (personalNotes.length > 0) {
        splitDomains.push({
          domain: 'personal-notes',
          results: personalNotes
        })
      }
      
      if (crmNotes.length > 0) {
        splitDomains.push({
          domain: 'notes',
          results: crmNotes
        })
      }
    } else if (domain.domain === 'tasks') {
      // Split tasks into personal-tasks and tasks (CRM)
      const personalTasks: SupersearchItem[] = []
      const crmTasks: SupersearchItem[] = []
      
      domain.results.forEach(item => {
        const context = item.item.context
        // Check if it's a personal task:
        // 1. Explicit context === 'personal'
        // 2. Task belongs to a personal project (parent_type === 'project' and parent_uuid is in personalProjectUuids)
        const isPersonal = context === 'personal' ||
                          (item.item.parent_type === 'project' && 
                           item.item.parent_uuid && 
                           personalProjectUuids.has(item.item.parent_uuid))
        
        if (isPersonal) {
          personalTasks.push(item)
        } else {
          // All other tasks go to CRM tasks
          crmTasks.push(item)
        }
      })
      
      if (personalTasks.length > 0) {
        splitDomains.push({
          domain: 'personal-tasks',
          results: personalTasks
        })
      }
      
      if (crmTasks.length > 0) {
        splitDomains.push({
          domain: 'tasks',
          results: crmTasks
        })
      }
    } else if (domain.domain === 'projects') {
      // Split projects into personal-projects and projects (CRM)
      const personalProjects: SupersearchItem[] = []
      const crmProjects: SupersearchItem[] = []
      
      domain.results.forEach(item => {
        // Check if it's a personal project
        // Personal projects have context === 'personal' OR (parent_type === 'user' AND projectable_uuid matches current user)
        // Just being owned by current user doesn't make it personal - CRM projects can be owned by users
        const isPersonal = item.item.context === 'personal' ||
                          (item.item.parent_type === 'user' && 
                           item.item.parent_uuid === currentUserUuid.value)
        
        if (isPersonal) {
          personalProjects.push(item)
        } else {
          // All other projects (crm, reporting, or no context) go to CRM projects
          crmProjects.push(item)
        }
      })
      
      if (personalProjects.length > 0) {
        splitDomains.push({
          domain: 'personal-projects',
          results: personalProjects
        })
      }
      
      if (crmProjects.length > 0) {
        splitDomains.push({
          domain: 'projects',
          results: crmProjects
        })
      }
    } else {
      // Keep other domains as-is
      splitDomains.push(domain)
    }
  }
  
  return splitDomains
}

/**
 * Filter domains based on permissions
 */
const filteredDomains = computed(() => {
  // First split notes and projects by context
  const splitResults = splitDomainsByContext(props.results)
  
  return splitResults.filter(domain => {
    switch (domain.domain) {
      case 'products':
        return hasPermission(PERMISSIONS.PIM.PRODUCTS.VIEW)
      case 'accounts':
        return hasPermission(PERMISSIONS.CRM.ACCOUNTS.VIEW)
      case 'sub-accounts':
        return hasPermission(PERMISSIONS.CRM.SUBACCOUNTS.VIEW)
      case 'tasks':
        // CRM tasks require permission
        // hasPermission handles super admin bypass automatically
        return hasPermission(PERMISSIONS.CRM.TASKS.VIEW)
      case 'personal-tasks':
        // Personal tasks always allowed
        return true
      case 'projects':
        // CRM projects require permission
        // hasPermission handles super admin bypass automatically
        return hasPermission(PERMISSIONS.CRM.PROJECTS.VIEW)
      case 'personal-projects':
        // Personal projects always allowed
        return true
      case 'addresses':
        // hasPermission handles super admin bypass automatically
        return hasPermission(PERMISSIONS.CRM.ADDRESSES.VIEW)
      case 'notes':
        // CRM notes require permission
        // hasPermission handles super admin bypass automatically
        return hasPermission(PERMISSIONS.CRM.NOTES.VIEW)
      case 'personal-notes':
        // Personal notes always allowed
        return true
      case 'users':
        // admin-only user listing
        return hasPermission(PERMISSIONS.ADMIN.USERS.VIEW)
      case 'bug-reports':
        // admin only - use permission check, not role check
        return hasPermission(PERMISSIONS.ADMIN.BUGREPORTS.VIEW) || hasPermission(PERMISSIONS.SYSTEM.SUPER_ADMIN)
      case 'operations-jobs':
        // Operations jobs require operations.jobs.view permission
        // hasPermission handles super admin bypass automatically
        return hasPermission(PERMISSIONS.OPERATIONS.JOBS.VIEW)
      case 'sales-history':
        // Sales history requires PERMISSIONS.SALESHISTORY.VIEW (canonical saleshistory.saleshistory.view)
        // hasPermission handles super admin bypass automatically
        return hasPermission(PERMISSIONS.SALESHISTORY.VIEW)
      default:
        return true
    }
  })
})

/**
 * Format parent type for display
 */
const formatParentType = (type?: string): string => {
  const labels: Record<string, string> = {
    project: 'Project',
    account: 'Account',
    'sub-account': 'Sub-Account',
    user: 'User',
  }
  return type ? (labels[type] || type) : ''
}

/**
 * Get route for parent type
 * @param type - Parent type (project, account, sub-account, user)
 * @param uuid - Parent UUID
 * @param item - Optional item data for context-aware routing (for projects)
 */
const getRouteForParentType = (type: string, uuid: string, item?: SupersearchItem['item']): string => {
  // Special handling for projects - use getProjectRoute if item data available
  if (type === 'project' && item) {
    return getProjectRoute({
      uuid,
      context: item.context as string | undefined,
      projectable_type: item.projectable_type as string | undefined
    })
  }
  
  const map: Record<string, string> = {
    project: `/crm/projects/${uuid}`, // Fallback if no item data
    account: `/crm/accounts/${uuid}`,
    'sub-account': `/crm/sub-accounts/${uuid}`,
    user: `/admin/users/${uuid}`,
  }
  return map[type] || '/'
}

/**
 * Handle item mousedown - set selecting flag and trigger click
 */
const handleItemMousedown = (item: SupersearchItem, domain: string) => {
  emit('selecting-start')
  handleItemClick(item, domain)
}

/**
 * Handle item click - navigate to item's own detail page
 */
const handleItemClick = async (item: SupersearchItem, domain: string) => {
  const data = item.item
  const currentPath = router.currentRoute.value.path
  
  try {
    let targetPath = ''
    
    // For operations-jobs, navigate to jobs list with search query
    if (domain === 'operations-jobs') {
      const query = props.query.trim()
      if (query) {
        targetPath = '/machines/jobs'
        await router.push({
          path: targetPath,
          query: { search: query }
        })
      } else {
        targetPath = '/machines/jobs'
        await router.push(targetPath)
      }
      // Only close if route actually changed
      if (router.currentRoute.value.path !== currentPath) {
        emit('close')
      }
      emit('selecting-end')
      return
    }
    
    // For sales-history, navigate to sales history list with search query
    if (domain === 'sales-history') {
      const query = props.query.trim()
      if (query) {
        targetPath = '/sales/sales-history'
        await router.push({
          path: targetPath,
          query: { search: query }
        })
      } else {
        targetPath = '/sales/sales-history'
        await router.push(targetPath)
      }
      // Only close if route actually changed
      if (router.currentRoute.value.path !== currentPath) {
        emit('close')
      }
      emit('selecting-end')
      return
    }
    
    // For notes, use getNoteRoute to determine correct route based on context
    if (domain === 'notes' || domain === 'personal-notes') {
      if (data.uuid) {
        // Create a note-like object with context for route helper
        const noteLike = {
          uuid: data.uuid,
          context: data.context as 'personal' | 'crm' | 'flash_report' | 'personal_project' | undefined,
          notable_type: data.parent_type === 'project' ? 'App\\Models\\Project' : 
                       data.parent_type === 'user' ? 'App\\Models\\User' : undefined,
          notable_uuid: data.parent_uuid
        }
        targetPath = getNoteRoute(noteLike as any)
        await router.push(targetPath)
        // Only close if route actually changed
        if (router.currentRoute.value.path !== currentPath) {
          emit('close')
        }
        emit('selecting-end')
        return
      }
    }
    
    // For tasks, use getTaskRoute to determine correct route based on context
    if (domain === 'tasks' || domain === 'personal-tasks') {
      if (data.uuid) {
        // Create a task-like object with context for route helper
        const taskLike = {
          uuid: data.uuid,
          context: data.context as 'personal' | 'crm' | undefined
        }
        targetPath = getTaskRoute(taskLike as any)
        await router.push(targetPath)
        // Only close if route actually changed
        if (router.currentRoute.value.path !== currentPath) {
          emit('close')
        }
        emit('selecting-end')
        return
      }
    }
    
    // For addresses, navigate to parent if available (addresses don't have detail pages)
    if (domain === 'addresses') {
      if (data.parent_link) {
        targetPath = convertApiLinkToRoute(data.parent_link, data)
        await router.push(targetPath)
        // Only close if route actually changed
        if (router.currentRoute.value.path !== currentPath) {
          emit('close')
        }
        emit('selecting-end')
        return
      }
      if (data.parent_type && data.parent_uuid) {
        targetPath = getRouteForParentType(data.parent_type, data.parent_uuid, data)
        await router.push(targetPath)
        // Only close if route actually changed
        if (router.currentRoute.value.path !== currentPath) {
          emit('close')
        }
        emit('selecting-end')
        return
      }
    }
    
    // For all other domains (including projects, sub-accounts, accounts, products, etc.), navigate to item's own link
    targetPath = convertApiLinkToRoute(item.link, item.item)
    await router.push(targetPath)
    // Only close if route actually changed
    if (router.currentRoute.value.path !== currentPath) {
      emit('close')
    }
    emit('selecting-end')
  } catch (error) {
    // Navigation failed (e.g., duplicate navigation, invalid route)
    console.warn('Navigation failed:', error)
    emit('selecting-end')
    // Don't emit close - let user try again
  }
}

/**
 * Handle show all results
 */
const handleShowAllResults = () => {
  router.push({
    name: 'supersearch',
    query: { q: props.query }
  })
  emit('close')
}

/**
 * Handle Escape key
 */
const handleEscape = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    emit('close')
  }
}

/**
 * Handle click outside
 */
const handleClickOutside = (event: MouseEvent) => {
  if (!panelRef.value) return
  
  const target = event.target as Node
  // Check if click is outside the panel
  if (!panelRef.value.contains(target)) {
    // Also check if click is not on the search input or its container
    const searchInputContainer = panelRef.value.closest('.relative')
    if (searchInputContainer && !searchInputContainer.contains(target)) {
      emit('close')
    }
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleEscape)
  // Use a small delay to avoid immediate close on mount
  setTimeout(() => {
    document.addEventListener('click', handleClickOutside, true)
  }, 0)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleEscape)
  document.removeEventListener('click', handleClickOutside, true)
})
</script>

<style scoped>
.supersearch-quick-panel {
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  right: 0;
  width: 100%;
  max-width: 100%;
  max-height: 70vh;
  background-color: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 0.5rem;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 1000;
}

.supersearch-quick-panel:not(.supersearch-quick-panel--inline) {
  left: -1rem;
  right: auto;
  width: calc(100% + 2rem);
  max-width: none;
}

.supersearch-quick-panel--inline {
  position: relative;
  top: auto;
  left: auto;
  right: auto;
  max-height: none;
  border: none;
  box-shadow: none;
  border-radius: 0;
}

.supersearch-panel-loading {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 0.75rem;
  min-height: 200px;
}

.supersearch-panel-results {
  flex: 1 1 auto;
  min-height: 200px;
  max-height: calc(70vh - 4.5rem);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.supersearch-quick-panel--inline .supersearch-panel-results {
  min-height: 0;
  max-height: none;
}

.supersearch-panel-results-scroll {
  flex: 1;
  min-height: 0;
}

.supersearch-panel-results-scroll :deep(.base-scrollbar-content) {
  padding: 0.5rem;
}

.supersearch-domain-section {
  margin-bottom: 1rem;
}

.supersearch-domain-section:last-child {
  margin-bottom: 0;
}

.supersearch-domain-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--color-text-muted);
  letter-spacing: 0.05em;
}

.supersearch-domain-label {
  color: var(--color-text-muted);
}

.supersearch-domain-count {
  color: var(--color-text);
}

.supersearch-domain-items {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.supersearch-item {
  padding: 0.75rem;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.15s;
  border: 1px solid transparent;
}

.supersearch-item:hover {
  background-color: var(--color-surface-hover);
  border-color: var(--color-border);
}

.supersearch-item-primary {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-text);
  margin-bottom: 0.25rem;
}

.supersearch-item-context {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  margin-bottom: 0.25rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.supersearch-item-secondary {
  font-size: 0.75rem;
  color: var(--color-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.supersearch-panel-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

.supersearch-panel-empty-content {
  text-align: center;
}

.supersearch-panel-footer {
  padding: 1rem;
  border-top: 1px solid var(--color-border);
}
</style>

